import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { getContacts, createContact, getTeamMembers } from '../utils/api';
import ContactFormModal from '../components/ContactFormModal';

const CATEGORY_LABELS = {
  lp_cogp: 'LP / Co-GP',
  lender: 'Lender',
  broker: 'Broker',
  partner_service: 'Partner',
  seller_operator: 'Seller / Operator',
};

const CATEGORY_BADGE_CLS = {
  lp_cogp: 'badge-green',
  lender: 'badge-blue',
  broker: 'badge-purple',
  partner_service: 'badge-amber',
  seller_operator: 'badge-teal',
};

const SORT_OPTIONS = [
  { value: 'last_name',           label: 'Name (A–Z)' },
  { value: 'last_interaction_date', label: 'Last interaction' },
  { value: 'created_at',          label: 'Recently added' },
  { value: 'organization',        label: 'Organization' },
];

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const diff = Date.now() - d;
  if (diff < 86400000)   return 'Today';
  if (diff < 172800000)  return 'Yesterday';
  if (diff < 604800000)  return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function ContactListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts,      setContacts]      = useState([]);
  const [pagination,    setPagination]    = useState({ total: 0, page: 1, per_page: 50, total_pages: 0 });
  const [availableTags, setAvailableTags] = useState([]);
  const [teamMembers,   setTeamMembers]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showCreate,    setShowCreate]    = useState(false);

  // All filters from URL params
  const search    = searchParams.get('search')   || '';
  const category  = searchParams.get('category') || '';
  const ownerId   = searchParams.get('owner_id') || '';
  const sortBy    = searchParams.get('sort_by')  || 'last_name';
  const sortDir   = searchParams.get('sort_dir') || 'asc';
  const page      = parseInt(searchParams.get('page') || '1');

  // Active tag filters — comma-separated in URL
  const activeTags = (searchParams.get('tags') || '').split(',').filter(Boolean);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getContacts({
        search, category, owner_id: ownerId,
        tags: activeTags.length ? activeTags.join(',') : undefined,
        sort_by: sortBy, sort_dir: sortDir,
        page, per_page: 50,
      });
      setContacts(res.data || []);
      setPagination(res.pagination || {});
      setAvailableTags(res.available_tags || []);
    } catch (err) {
      console.error('Load contacts error:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-line
  }, [search, category, ownerId, sortBy, sortDir, page, searchParams.get('tags')]);

  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => {
    getTeamMembers().then(res => setTeamMembers(res.data || res)).catch(console.error);
  }, []);

  const setParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  };

  const toggleTag = (tag) => {
    const next = activeTags.includes(tag)
      ? activeTags.filter(t => t !== tag)
      : [...activeTags, tag];
    const p = new URLSearchParams(searchParams);
    if (next.length) p.set('tags', next.join(',')); else p.delete('tags');
    p.delete('page');
    setSearchParams(p);
  };

  const clearTags = () => {
    const p = new URLSearchParams(searchParams);
    p.delete('tags');
    p.delete('page');
    setSearchParams(p);
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setParam('sort_dir', sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      const p = new URLSearchParams(searchParams);
      p.set('sort_by', col);
      p.set('sort_dir', col === 'last_interaction_date' || col === 'created_at' ? 'desc' : 'asc');
      p.delete('page');
      setSearchParams(p);
    }
  };

  const handleCreate = async (data) => {
    await createContact(data);
    setShowCreate(false);
    loadContacts();
  };

  const sortArrow = (col) => {
    if (sortBy !== col) return <span style={{ color: 'var(--color-border)', marginLeft: 3 }}>↕</span>;
    return <span style={{ color: 'var(--color-text-primary)', marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Contacts{category ? ` · ${CATEGORY_LABELS[category]}` : ''}
          </h1>
          <p className="page-subtitle">{pagination.total} contact{pagination.total !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/contacts/bulk-upload" className="btn">
            Bulk upload
          </Link>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New Contact
          </button>
        </div>
      </div>

      {/* Filters row */}
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <input
          className="search-input"
          type="text"
          placeholder="Search name, org, notes, tags…"
          value={search}
          onChange={e => setParam('search', e.target.value)}
        />
        <select className="filter-select" value={category} onChange={e => setParam('category', e.target.value)}>
          <option value="">All categories</option>
          <option value="lp_cogp">LP / Co-GP</option>
          <option value="lender">Lender</option>
          <option value="broker">Broker</option>
          <option value="partner_service">Partner / Service</option>
          <option value="seller_operator">Seller / Operator</option>
        </select>
        <select className="filter-select" value={ownerId} onChange={e => setParam('owner_id', e.target.value)}>
          <option value="">All owners</option>
          {teamMembers.map(m => (
            <option key={m.id} value={m.id}>{m.display_name}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={`${sortBy}:${sortDir}`}
          onChange={e => {
            const [col, dir] = e.target.value.split(':');
            const p = new URLSearchParams(searchParams);
            p.set('sort_by', col); p.set('sort_dir', dir); p.delete('page');
            setSearchParams(p);
          }}
        >
          {SORT_OPTIONS.map(o => (
            <React.Fragment key={o.value}>
              <option value={`${o.value}:asc`}>{o.label} ↑</option>
              <option value={`${o.value}:desc`}>{o.label} ↓</option>
            </React.Fragment>
          ))}
        </select>
      </div>

      {/* Tag pills */}
      {availableTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, alignItems: 'center' }}>
          {availableTags.map(({ tag, count }) => {
            const active = activeTags.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                style={{
                  padding: '3px 10px',
                  borderRadius: 12,
                  border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  background: active ? 'var(--color-accent-light)' : 'transparent',
                  color: active ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-ui)',
                  fontWeight: active ? 600 : 400,
                }}
              >
                {tag}
                <span style={{ marginLeft: 5, opacity: 0.6, fontSize: '0.7rem' }}>
                  {count}
                </span>
              </button>
            );
          })}
          {activeTags.length > 0 && (
            <button
              onClick={clearTags}
              style={{
                fontSize: '0.75rem', color: 'var(--color-text-muted)',
                background: 'none', border: 'none', cursor: 'pointer',
                textDecoration: 'underline', fontFamily: 'var(--font-ui)',
              }}
            >
              Clear tags
            </button>
          )}
          {activeTags.length > 0 && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              — showing contacts matching {activeTags.length > 1 ? 'any of: ' : ''}
              <strong>{activeTags.join(', ')}</strong>
            </span>
          )}
        </div>
      )}

      {/* Sort header row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 120px 100px 80px',
        padding: '5px 16px',
        fontSize: '0.72rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        color: 'var(--color-text-muted)',
        borderBottom: '1px solid var(--color-border)',
        marginBottom: 0,
      }}>
        <button onClick={() => toggleSort('last_name')} style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', fontFamily: 'var(--font-ui)', padding: 0 }}>
          Name{sortArrow('last_name')}
        </button>
        <button onClick={() => toggleSort('organization')} style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', fontFamily: 'var(--font-ui)', padding: 0 }}>
          Org{sortArrow('organization')}
        </button>
        <span>Owner</span>
        <button onClick={() => toggleSort('last_interaction_date')} style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'right', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', fontFamily: 'var(--font-ui)', padding: 0 }}>
          Last contact{sortArrow('last_interaction_date')}
        </button>
      </div>

      {/* Contact list */}
      <div className="card" style={{ padding: 0, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        {loading ? (
          <div className="page-loading">Loading contacts…</div>
        ) : contacts.length === 0 ? (
          <div className="empty-state">
            <p>{search || category || ownerId || activeTags.length ? 'No contacts match your filters.' : 'Add your first contact to get started.'}</p>
            {(search || category || ownerId || activeTags.length) && (
              <button className="btn" onClick={() => setSearchParams(new URLSearchParams())}>Clear all filters</button>
            )}
          </div>
        ) : (
          contacts.map((c, i) => (
            <div
              key={c.id}
              onClick={() => navigate(`/contacts/${c.id}`)}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 100px 80px',
                alignItems: 'center',
                padding: '10px 16px',
                borderBottom: i < contacts.length - 1 ? '1px solid var(--color-border-faint)' : 'none',
                cursor: 'pointer',
                gap: 8,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {/* Name + category + tags */}
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div className={`contact-avatar avatar-${c.category}`} style={{ width: 30, height: 30, fontSize: '0.72rem', flexShrink: 0 }}>
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {c.first_name} {c.last_name}
                      </span>
                      <span className={`badge ${CATEGORY_BADGE_CLS[c.category] || 'badge-neutral'}`}>
                        {CATEGORY_LABELS[c.category]}
                      </span>
                      {parseInt(c.closed_deal_count) > 0 && (
                        <span className="badge badge-green">
                          {c.closed_deal_count} closed
                        </span>
                      )}
                    </div>
                    {/* Tags */}
                    {c.tags && c.tags.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {c.tags.map(tag => (
                          <span
                            key={tag}
                            onClick={e => { e.stopPropagation(); toggleTag(tag); }}
                            style={{
                              padding: '1px 7px',
                              borderRadius: 10,
                              fontSize: '0.7rem',
                              background: activeTags.includes(tag) ? 'var(--color-accent-light)' : 'var(--color-bg)',
                              color: activeTags.includes(tag) ? 'var(--color-accent)' : 'var(--color-text-muted)',
                              border: `1px solid ${activeTags.includes(tag) ? 'var(--color-accent)' : 'var(--color-border)'}`,
                              cursor: 'pointer',
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Organization */}
              <div style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.organization || '—'}
              </div>

              {/* Owner */}
              <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>
                {c.owner_name || '—'}
              </div>

              {/* Last interaction */}
              <div style={{ textAlign: 'right', fontSize: '0.78rem', color: c.last_interaction_date ? 'var(--color-text-secondary)' : 'var(--color-text-muted)' }}>
                {formatDate(c.last_interaction_date) || 'Never'}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button className="btn btn-sm" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>
            Previous
          </button>
          <span>Page {pagination.page} of {pagination.total_pages}</span>
          <button className="btn btn-sm" disabled={page >= pagination.total_pages} onClick={() => setParam('page', String(page + 1))}>
            Next
          </button>
        </div>
      )}

      {showCreate && (
        <ContactFormModal
          teamMembers={teamMembers}
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
